import { getApiUrl } from './apiConfig';

const PUBLIC_CATALOG_PREFIXES = [
  'curated/',
  'scientific_papers/',
  'graphic/',
  'classics/',
  'short_stories/',
  'explainers/'
];

const isPublicCatalogKey = (key) => {
  const normalized = String(key || '').trim();
  if (!normalized) return false;
  return PUBLIC_CATALOG_PREFIXES.some((prefix) => normalized.startsWith(prefix));
};

class WasabiService {
  constructor() {
    this.authContext = null;
    this.cache = new Map();
    this.cacheTTL = 5 * 60 * 1000; // 5 minutes cache
    this.missingStorageKeys = new Map();
    this.missingStorageTTL = 30 * 60 * 1000; // 30 minutes
    this.signedUrlCache = new Map();
  }

  setAuthContext(authContext) {
    this.authContext = authContext;
  }

  getCacheKey(key) {
    return `wasabi:${key}`;
  }

  getFromCache(key) {
    const cacheKey = this.getCacheKey(key);
    const cached = this.cache.get(cacheKey);
    if (cached && Date.now() - cached.timestamp < this.cacheTTL) {
      console.log('Cache hit for:', key);
      return cached.data;
    }
    return null;
  }

  setInCache(key, data) {
    const cacheKey = this.getCacheKey(key);
    this.cache.set(cacheKey, {
      data,
      timestamp: Date.now()
    });
  }

  getSignedUrlFromCache(key) {
    const cached = this.signedUrlCache.get(String(key || ''));
    if (!cached) {
      return '';
    }
    if (Date.now() >= Number(cached.expiresAt || 0) - 60 * 1000) {
      this.signedUrlCache.delete(String(key || ''));
      return '';
    }
    return String(cached.url || '');
  }

  setSignedUrlInCache(key, url, expiresAtMs) {
    this.signedUrlCache.set(String(key || ''), {
      url: String(url || ''),
      expiresAt: Number(expiresAtMs || 0)
    });
  }

  async makeRequest(path, options = {}, requireAuth = true, suppressErrorLog = false) {
    try {
      let headers = {};
      
      if (requireAuth) {
        if (!this.authContext) {
          throw new Error('Auth context not set');
        }

        const { currentUser, getToken } = this.authContext;
        if (!currentUser) {
          throw new Error('No authenticated user');
        }

        // Get initial token
        const token = await getToken();
        if (!token) {
          throw new Error('Failed to get authentication token');
        }

        headers['Authorization'] = `Bearer ${token}`;
      }

      // Don't set Content-Type for FormData (browser will set it with boundary)
      if (!(options.body instanceof FormData)) {
        headers['Content-Type'] = 'application/json';
      }

      const url = getApiUrl(path);
      console.log('Making request to:', url);

      // Make request
      const response = await fetch(url, {
        ...options,
        headers: {
          ...options.headers,
          ...headers
        },
        credentials: 'include'
      });

      // If unauthorized and auth is required, try refreshing token
      if (requireAuth && response.status === 401) {
        console.log('Token expired, refreshing...');
        const newToken = await this.authContext.getToken(true);
        if (!newToken) {
          throw new Error('Failed to refresh authentication token');
        }

        // Retry with new token
        const retryResponse = await fetch(url, {
          ...options,
          headers: {
            ...options.headers,
            'Authorization': `Bearer ${newToken}`,
            'Content-Type': options.body instanceof FormData ? undefined : 'application/json'
          },
          credentials: 'include'
        });

        if (!retryResponse.ok) {
          let retryDetail = '';
          try {
            const retryType = retryResponse.headers.get('content-type') || '';
            if (retryType.includes('application/json')) {
              const retryBody = await retryResponse.json();
              retryDetail = String(retryBody?.detail || retryBody?.message || '').trim();
            } else {
              retryDetail = String(await retryResponse.text()).trim();
            }
          } catch (retryParseError) {
            retryDetail = '';
          }
          throw new Error(
            retryDetail
              ? `Request failed: ${retryResponse.status} - ${retryDetail}`
              : `Request failed: ${retryResponse.status}`
          );
        }

        return retryResponse;
      }

      if (!response.ok) {
        let detail = '';
        try {
          const contentType = response.headers.get('content-type') || '';
          if (contentType.includes('application/json')) {
            const body = await response.json();
            detail = String(body?.detail || body?.message || '').trim();
          } else {
            detail = String(await response.text()).trim();
          }
        } catch (parseError) {
          detail = '';
        }
        throw new Error(detail ? `Request failed: ${response.status} - ${detail}` : `Request failed: ${response.status}`);
      }

      return response;
    } catch (error) {
      if (!suppressErrorLog) {
        console.error('Request error:', error);
      }
      throw error;
    }
  }

  extractStorageKey(value) {
    const raw = String(value || '').trim();
    if (!raw) return null;

    // Already a presigned storage URL — use it directly instead of
    // re-routing the download through the backend.
    if (raw.includes('X-Amz-')) return null;

    if (raw.startsWith('users/') || isPublicCatalogKey(raw)) {
      return decodeURIComponent(raw);
    }

    if (raw.includes('/api/wasabi/file/')) {
      const keyPart = raw.split('/api/wasabi/file/')[1]?.split('?')[0] || '';
      return keyPart ? decodeURIComponent(keyPart) : null;
    }

    if (!raw.startsWith('http://') && !raw.startsWith('https://')) {
      return null;
    }

    try {
      const parsed = new URL(raw);
      const pathname = decodeURIComponent(parsed.pathname.replace(/^\/+/, ''));
      if (pathname.startsWith('users/') || isPublicCatalogKey(pathname)) {
        return pathname;
      }
      return null;
    } catch (error) {
      console.warn('Unable to parse storage URL for key extraction:', error);
      return null;
    }
  }

  /**
   * True when the browser can load this image straight from storage or the
   * CDN: a presigned URL, or a public CDN URL. Such covers must never be
   * pulled through the backend (that turned every cover on the dashboard
   * into a Render request). Unsigned links to the private bucket, bare keys
   * and our own /api/ paths still need the backend.
   */
  isDirectImageUrl(value) {
    const raw = String(value || '').trim();
    if (!/^https?:\/\//i.test(raw)) return false;
    if (raw.includes('X-Amz-')) return true;
    if (raw.includes('/api/')) return false;
    try {
      const host = new URL(raw).hostname;
      return !/wasabisys\.com$/i.test(host);
    } catch (e) {
      return false;
    }
  }

  /** Ask the backend to make the small copy of a cover (once per cover per session). */
  requestCoverThumb(coverReference) {
    const ref = String(coverReference || '').trim();
    if (!ref) return;
    this.thumbRequests = this.thumbRequests || new Set();
    const id = ref.split('?')[0];
    if (this.thumbRequests.has(id)) return;
    this.thumbRequests.add(id);
    this.makeRequest('/api/covers/thumb', { method: 'POST', body: JSON.stringify({ cover: ref }) }, true, true).catch(() => {});
  }

  async resolveCoverImageSource(coverReference) {
    const raw = String(coverReference || '').trim();
    if (!raw) {
      return { src: '', shouldRevoke: false };
    }
    if (this.isDirectImageUrl(raw)) {
      return { src: raw, shouldRevoke: false };
    }

    const storageKey = this.extractStorageKey(raw);
    if (!storageKey) {
      return { src: raw, shouldRevoke: false };
    }

    const missingAt = this.missingStorageKeys.get(storageKey);
    if (missingAt && Date.now() - missingAt < this.missingStorageTTL) {
      return { src: '', shouldRevoke: false };
    }

    const blob = await this.downloadFile(storageKey, {
      suppressErrorLog: true,
      suppressNotFound: true
    });
    if (!blob) {
      this.missingStorageKeys.set(storageKey, Date.now());
      return { src: '', shouldRevoke: false };
    }
    const objectUrl = URL.createObjectURL(blob);
    return { src: objectUrl, shouldRevoke: true };
  }

  async getSignedFileUrl(key, options = {}) {
    const {
      expiresIn = 43200,
      suppressErrorLog = false,
      suppressNotFound = false
    } = options;

    const normalizedKey = String(key || '').trim();
    if (!normalizedKey) {
      return '';
    }

    const cachedUrl = this.getSignedUrlFromCache(normalizedKey);
    if (cachedUrl) {
      return cachedUrl;
    }

    try {
      const requireAuth = !isPublicCatalogKey(normalizedKey);
      const response = await this.makeRequest(
        `/api/wasabi/presign/${encodeURIComponent(normalizedKey)}?expiresIn=${encodeURIComponent(expiresIn)}`,
        {},
        requireAuth,
        true
      );
      const result = await response.json();
      const signedUrl = String(result?.url || '');
      if (!signedUrl) {
        return '';
      }

      const expiresAtEpoch = Number(result?.expiresAt || 0);
      const expiresAtMs = expiresAtEpoch > 0
        ? (expiresAtEpoch * 1000)
        : (Date.now() + (Number(result?.expiresIn || expiresIn) * 1000));
      this.setSignedUrlInCache(normalizedKey, signedUrl, expiresAtMs);
      return signedUrl;
    } catch (error) {
      if (suppressNotFound && String(error?.message || '').includes('404')) {
        return '';
      }
      if (!suppressErrorLog) {
        console.error('Error getting signed file URL:', error);
      }
      throw error;
    }
  }

  // Alias for backward compatibility
  async makeAuthenticatedRequest(path, options = {}) {
    return this.makeRequest(path, options, true);
  }

  async listAudiobooks(userId) {
    try {
      console.log('Listing audiobooks');
      const cacheKey = `audiobooks:${userId}`;
      const cached = this.getFromCache(cacheKey);
      if (cached) {
        return cached;
      }

      // List all files under the user's audiobooks directory
      const prefix = `users/${userId}/audiobooks/`;
      console.log('Making list request with prefix:', prefix);
      const response = await this.makeAuthenticatedRequest(`/api/wasabi/list?prefix=${encodeURIComponent(prefix)}`);
      const files = await response.json();
      
      // Find all manifest.json files
      const manifestFiles = files.files.filter(file => file.key.endsWith('/manifest.json'));
      
      // Batch fetch manifests
      const manifestPromises = manifestFiles.map(file => 
        this.makeAuthenticatedRequest(`/api/wasabi/file/${encodeURIComponent(file.key)}`)
          .then(res => res.json())
          .catch(err => {
            console.error(`Error fetching manifest ${file.key}:`, err);
            return null;
          })
      );
      
      const audiobooks = (await Promise.all(manifestPromises)).filter(Boolean);
      
      this.setInCache(cacheKey, audiobooks);
      console.log('Received audiobooks:', audiobooks.length);
      return audiobooks;
    } catch (error) {
      console.error('Error listing audiobooks:', error);
      throw error;
    }
  }

  async listAudiobookMetadata() {
    try {
      const response = await this.makeAuthenticatedRequest('/api/metadata/audiobooks');
      const result = await response.json();
      return result.items || [];
    } catch (error) {
      console.error('Error listing audiobook metadata:', error);
      return [];
    }
  }

  async listLibrary() {
    try {
      const response = await this.makeAuthenticatedRequest('/api/library');
      const result = await response.json();
      return result.items || [];
    } catch (error) {
      console.error('Error listing library:', error);
      throw error;
    }
  }

  async getDashboard() {
    // One round-trip for the whole dashboard: user library + curated catalog
    // (with per-book playback state) + presigned cover URLs.
    const response = await this.makeAuthenticatedRequest('/api/dashboard');
    const result = await response.json();
    return {
      library: Array.isArray(result?.library) ? result.library : [],
      curated: Array.isArray(result?.curated) ? result.curated : [],
      insights: result?.insights && typeof result.insights === 'object' ? result.insights : null,
      // Story Time Premium: draws the locks before the stream endpoint refuses.
      subscriptionActive: Boolean(result?.subscriptionActive)
    };
  }

  async setGoal(weeklyMinutes) {
    try {
      const response = await this.makeAuthenticatedRequest('/api/goal', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ weeklyMinutes })
      });
      return await response.json();
    } catch (error) {
      console.error('Error saving weekly goal:', error);
      throw error;
    }
  }

  async getAudiobookMetadata(audiobookId) {
    try {
      const response = await this.makeAuthenticatedRequest(
        `/api/metadata/audiobooks/${encodeURIComponent(audiobookId)}`
      );
      return await response.json();
    } catch (error) {
      console.error('Error getting audiobook metadata:', error);
      return null;
    }
  }

  async getAudiobook(userId, audiobookId) {
    try {
      console.log('Getting audiobook:', audiobookId);
      const cacheKey = `audiobook:${userId}:${audiobookId}`;
      const cached = this.getFromCache(cacheKey);
      if (cached) {
        return cached;
      }

      // DB-first metadata fetch for player/detailed reads.
      // Keep legacy manifest fallback for older records during migration.
      const metadata = await this.getAudiobookMetadata(audiobookId);
      if (metadata && typeof metadata === 'object') {
        const metadataChapters = Array.isArray(metadata.chapters) ? metadata.chapters : [];
        let chapters = metadataChapters
          .map((chapter, index) => ({
            ...chapter,
            id: String(chapter?.id ?? chapter?.chapterId ?? index + 1),
            title: chapter?.title || `Chapter ${index + 1}`,
            order: Number(chapter?.order || chapter?.chapter_order || index + 1),
            duration: Number(chapter?.duration || 0),
            status: chapter?.status || '',
            audioUrl: chapter?.audioUrl || chapter?.audio_url || chapter?.audioKey || chapter?.audio_key || '',
            audio_url: chapter?.audio_url || chapter?.audioUrl || chapter?.audioKey || chapter?.audio_key || '',
            transcriptUrl: chapter?.transcriptUrl || chapter?.transcript_url || chapter?.transcriptKey || chapter?.transcript_key || '',
            transcript_url: chapter?.transcript_url || chapter?.transcriptUrl || chapter?.transcriptKey || chapter?.transcript_key || ''
          }))
          .sort((a, b) => Number(a.order || 0) - Number(b.order || 0));

        if (!chapters.length && Number(metadata.totalChapters || 0) > 0) {
          chapters = Array.from({ length: Number(metadata.totalChapters || 0) }, (_, idx) => ({
            id: String(idx + 1),
            title: `Chapter ${idx + 1}`,
            order: idx + 1,
            duration: 0,
            status: ''
          }));
        }

        const audiobook = {
          id: metadata.id || audiobookId,
          title: metadata.title || 'Untitled',
          name: metadata.title || 'Untitled',
          status: metadata.status || '',
          progress: Number(metadata.progress || 0),
          completedChapters: Number(metadata.completedChapters || 0),
          totalChapters: Number(metadata.totalChapters || chapters.length || 0),
          totalDuration: Number(metadata.totalDuration || 0),
          documentType: metadata.documentType || metadata.document_type || '',
          coverUrl: metadata.coverUrl || '',
          fallbackQuote: metadata.fallbackQuote || '',
          voiceSettings: metadata.voice ? { voice: metadata.voice } : {},
          voiceTier: metadata.voiceTier || metadata.voice_tier || '',
          speakerSplit: Boolean(metadata.speakerSplit),
          nsfw: Boolean(metadata.nsfw),
          isStreaming: metadata.isStreaming !== false,
          chapters
        };

        this.setInCache(cacheKey, audiobook);
        console.log('Received audiobook data from DB metadata');
        return audiobook;
      }

      // Get manifest (legacy fallback path)
      const manifestKey = `users/${userId}/audiobooks/${audiobookId}/manifest.json`;
      const manifestResponse = await this.makeAuthenticatedRequest(`/api/wasabi/file/${encodeURIComponent(manifestKey)}`);
      const manifest = await manifestResponse.json();

      // Batch fetch chapter files, but gracefully support legacy manifests that
      // keep chapter data inline (or have partial/missing chapter JSON files).
      const manifestChapters = Array.isArray(manifest.chapters) ? manifest.chapters : [];
      const chapterPromises = manifestChapters.map(async (chapterEntry, index) => {
        const inlineChapter = (chapterEntry && typeof chapterEntry === 'object') ? chapterEntry : {};
        const rawChapterId = (chapterEntry && typeof chapterEntry === 'object')
          ? (chapterEntry.id ?? chapterEntry.chapterId ?? chapterEntry.order)
          : chapterEntry;
        const chapterId = rawChapterId !== undefined && rawChapterId !== null ? String(rawChapterId) : '';

        let fileChapter = null;
        if (chapterId) {
          const chapterKey = `users/${userId}/audiobooks/${audiobookId}/chapters/${chapterId}.json`;
          try {
            const chapterResponse = await this.makeAuthenticatedRequest(`/api/wasabi/file/${encodeURIComponent(chapterKey)}`);
            fileChapter = await chapterResponse.json();
          } catch (err) {
            console.warn(`Chapter JSON missing/unreadable for ${chapterId}, using manifest fallback if available:`, err);
          }
        }

        const mergedChapter = {
          ...inlineChapter,
          ...(fileChapter || {})
        };

        const resolvedIdValue =
          (mergedChapter.id ?? mergedChapter.chapterId ?? chapterId) || (index + 1);
        const resolvedId = String(resolvedIdValue);

        return {
          ...mergedChapter,
          id: resolvedId,
          title: mergedChapter.title || `Chapter ${index + 1}`,
          order: Number(mergedChapter.order || index + 1)
        };
      });

      const chapters = (await Promise.all(chapterPromises))
        .filter(Boolean)
        .sort((a, b) => Number(a.order || 0) - Number(b.order || 0));

      // Legacy compatibility: some older books do not store chapter audio/transcript
      // under predictable names (e.g., chapterId.mp3). Discover actual files and
      // backfill chapter pointers when missing.
      const needsMediaDiscovery = chapters.some(chapter => (
        (!chapter?.audioUrl && !chapter?.audio_url && !chapter?.audioKey && !chapter?.audio_key) ||
        (!chapter?.transcriptUrl && !chapter?.transcript_url && !chapter?.transcriptKey && !chapter?.transcript_key)
      ));

      if (needsMediaDiscovery) {
        try {
        const [audioListResponse, transcriptListResponse] = await Promise.all([
          this.makeAuthenticatedRequest(`/api/wasabi/list?prefix=${encodeURIComponent(`users/${userId}/audiobooks/${audiobookId}/audio/`)}`),
          this.makeAuthenticatedRequest(`/api/wasabi/list?prefix=${encodeURIComponent(`users/${userId}/audiobooks/${audiobookId}/transcripts/`)}`)
        ]);

        const audioFiles = ((await audioListResponse.json())?.files || [])
          .map(file => file?.key)
          .filter(Boolean)
          .sort();
        const transcriptFiles = ((await transcriptListResponse.json())?.files || [])
          .map(file => file?.key)
          .filter(Boolean)
          .sort();
        const transcriptSrtFiles = transcriptFiles
          .filter((key) => String(key || '').toLowerCase().endsWith('.srt'));
        const wordTimestampFiles = transcriptFiles
          .filter((key) => String(key || '').toLowerCase().endsWith('.words.json'));

        const findByChapterId = (keys, chapterId) => {
          const id = String(chapterId);
          const direct = keys.find(key => {
            const name = key.split('/').pop() || '';
            return name === id || name.startsWith(`${id}.`) || name.startsWith(`${id}_`) || name.startsWith(`${id}-`);
          });
          return direct || null;
        };

        chapters.forEach((chapter, index) => {
          if (!chapter?.audioUrl && !chapter?.audio_url && !chapter?.audioKey && !chapter?.audio_key) {
            const byId = findByChapterId(audioFiles, chapter.id);
            const byOrder = audioFiles[index] || null;
            if (byId || byOrder) {
              chapter.audioKey = byId || byOrder;
            }
          }

          if (!chapter?.transcriptUrl && !chapter?.transcript_url && !chapter?.transcriptKey && !chapter?.transcript_key) {
            const byId = findByChapterId(transcriptSrtFiles, chapter.id);
            const byOrder = transcriptSrtFiles[index] || null;
            if (byId || byOrder) {
              chapter.transcriptKey = byId || byOrder;
            }
          }

          if (!chapter?.wordTimestampsUrl && !chapter?.word_timestamps_url && !chapter?.wordTimestampsKey && !chapter?.word_timestamps_key) {
            const byId = findByChapterId(wordTimestampFiles, chapter.id);
            const byOrder = wordTimestampFiles[index] || null;
            if (byId || byOrder) {
              chapter.wordTimestampsKey = byId || byOrder;
            }
          }
        });
        } catch (fileDiscoveryError) {
          console.warn('Legacy media file discovery failed, proceeding with chapter defaults:', fileDiscoveryError);
        }
      }
      
      const audiobook = {
        ...manifest,
        chapters
      };
      
      this.setInCache(cacheKey, audiobook);
      console.log('Received audiobook data');
      return audiobook;
    } catch (error) {
      console.error('Error getting audiobook:', error);
      throw error;
    }
  }

  async deleteAudiobook(userId, audiobookId) {
    try {
      console.log('Deleting audiobook:', audiobookId);
      
      // List all files under this audiobook
      const prefix = `users/${userId}/audiobooks/${audiobookId}/`;
      const files = await this.listFiles(prefix);
      const manifestKey = `users/${userId}/audiobooks/${audiobookId}/manifest.json`;
      
      // Always include manifest key delete call so backend can remove DB metadata
      // even when storage objects are already missing (failed/partial jobs).
      const keysToDelete = new Set(files.map(file => file.key));
      keysToDelete.add(manifestKey);

      const deletePromises = Array.from(keysToDelete).map(key =>
        this.makeAuthenticatedRequest(`/api/wasabi/delete/${encodeURIComponent(key)}`, {
          method: 'DELETE'
        })
      );
      
      await Promise.all(deletePromises);
      
      // Clear cache
      this.cache.clear();
      
      console.log('Audiobook deleted successfully');
    } catch (error) {
      console.error('Error deleting audiobook:', error);
      throw error;
    }
  }

  async uploadFile(key, data, contentType) {
    try {
      console.log('Uploading file:', key, 'Content-Type:', contentType);
      
      let fileContent;
      
      // Handle JSON data
      if (contentType === 'application/json') {
        if (typeof data === 'string') {
          fileContent = JSON.stringify(JSON.parse(data), null, 2);
        } else if (data instanceof Uint8Array) {
          fileContent = JSON.stringify(JSON.parse(new TextDecoder().decode(data)), null, 2);
        } else {
          fileContent = JSON.stringify(data, null, 2);
        }
      } else {
        if (data instanceof Blob || data instanceof File) {
          fileContent = await data.text();
        } else if (data instanceof Uint8Array) {
          fileContent = new TextDecoder().decode(data);
        } else if (typeof data === 'string') {
          fileContent = data;
        } else {
          fileContent = String(data);
        }
      }

      // Create FormData
      const formData = new FormData();

      if (contentType === 'application/json') {
        try {
          const jsonData = typeof fileContent === 'string'
            ? JSON.parse(fileContent)
            : fileContent;

          const jsonString = JSON.stringify(jsonData, null, 2);
          const encoder = new TextEncoder();
          const jsonBytes = encoder.encode(jsonString);
          const file = new File([jsonBytes], 'manifest.json', {
            type: 'application/json'
          });

          formData.append('file', file);
        } catch (e) {
          console.error('JSON processing error:', e);
          throw new Error(`Invalid JSON data: ${e.message}`);
        }
      } else {
        const file = new File([fileContent], 'data', {
          type: contentType
        });
        formData.append('file', file);
      }

      formData.append('key', key);
      formData.append('contentType', contentType);

      const response = await this.makeAuthenticatedRequest('/api/wasabi/upload', {
        method: 'POST',
        body: formData
      });

      const result = await response.json();
      
      // Clear cache for this file and any parent listings
      const parts = key.split('/');
      const cacheKeys = [
        this.getCacheKey(key), // Clear file cache
      ];
      
      // If this is a manifest file, clear related caches
      if (key.endsWith('manifest.json')) {
        const userId = parts[1];
        const audiobookId = parts[3];
        cacheKeys.push(
          `audiobooks:${userId}`,
          `audiobook:${userId}:${audiobookId}`
        );
      }
      
      cacheKeys.forEach(key => {
        if (this.cache.has(key)) {
          this.cache.delete(key);
        }
      });
      
      console.log('File uploaded successfully');
      return result.url;
    } catch (error) {
      console.error('Error uploading file:', error);
      throw error;
    }
  }

  async downloadFile(key, options = {}) {
    const { suppressErrorLog = false, suppressNotFound = false } = options;
    try {
      console.log('Downloading file:', key);
      const cached = this.getFromCache(key);
      if (cached) {
        return cached;
      }

      // Skip authentication for curated content
      const requireAuth = !isPublicCatalogKey(key);
      const response = await this.makeRequest(
        `/api/wasabi/file/${encodeURIComponent(key)}`,
        {},
        requireAuth,
        suppressErrorLog
      );
      const blob = await response.blob();
      this.setInCache(key, blob);
      console.log('File downloaded successfully');
      return blob;
    } catch (error) {
      if (suppressNotFound && String(error?.message || '').includes('404')) {
        return null;
      }
      if (!suppressErrorLog) {
        console.error('Error downloading file:', error);
      }
      throw error;
    }
  }

  async listFiles(prefix) {
    try {
      console.log('Listing files with prefix:', prefix);
      const cacheKey = `files:${prefix}`;
      const cached = this.getFromCache(cacheKey);
      if (cached) {
        return cached;
      }

      const response = await this.makeAuthenticatedRequest(
        `/api/wasabi/list?prefix=${encodeURIComponent(prefix)}`
      );
      const result = await response.json();
      this.setInCache(cacheKey, result.files);
      console.log('Received file list:', result.files.length);
      return result.files;
    } catch (error) {
      console.error('Error listing files:', error);
      throw error;
    }
  }

  async listCuratedAudiobooks() {
    try {
      console.log('Listing curated audiobooks');
      const cacheKey = 'audiobooks:curated';
      const cached = this.getFromCache(cacheKey);
      if (cached) {
        return cached;
      }

      const response = await this.makeRequest('/api/wasabi/curated', {}, false);
      const books = await response.json();
      
      // Add isCurated flag to each book
      const curatedBooks = books.map(book => ({
        ...book,
        isCurated: true
      }));

      this.setInCache(cacheKey, curatedBooks);
      console.log('Received curated audiobooks:', curatedBooks.length);
      return curatedBooks;
    } catch (error) {
      console.error('Error listing curated audiobooks:', error);
      throw error;
    }
  }

  async getCuratedAudiobook(audiobookId) {
    try {
      console.log('Getting curated audiobook:', audiobookId);
      const cacheKey = `audiobook:curated:${audiobookId}`;
      const cached = this.getFromCache(cacheKey);
      if (cached) {
        return cached;
      }

      const response = await this.makeRequest(
        `/api/wasabi/curated/${encodeURIComponent(audiobookId)}`,
        {},
        false
      );
      const audiobook = await response.json();
      
      // Add isCurated flag
      const curatedAudiobook = {
        ...audiobook,
        isCurated: true
      };

      this.setInCache(cacheKey, curatedAudiobook);
      console.log('Received curated audiobook data');
      return curatedAudiobook;
    } catch (error) {
      console.error('Error getting curated audiobook:', error);
      throw error;
    }
  }

  generateKey(userId, audiobookId, type, filename) {
    const base = `users/${userId}/audiobooks/${audiobookId}`;
    if (type) {
      return `${base}/${type}/${filename}`;
    }
    return `${base}/${filename}`;
  }

  async getChapterPlaybackState(audiobookId, chapterId) {
    try {
      const response = await this.makeAuthenticatedRequest(
        `/api/playback/${encodeURIComponent(audiobookId)}/${encodeURIComponent(chapterId)}`
      );
      return await response.json();
    } catch (error) {
      console.error('Error getting chapter playback state:', error);
      throw error;
    }
  }

  async getAudiobookPlaybackState(audiobookId) {
    try {
      const response = await this.makeAuthenticatedRequest(
        `/api/playback/${encodeURIComponent(audiobookId)}`
      );
      return await response.json();
    } catch (error) {
      console.error('Error getting audiobook playback state:', error);
      throw error;
    }
  }

  async updateAudiobookPlaybackState(audiobookId, updates) {
    try {
      const response = await this.makeAuthenticatedRequest(
        `/api/playback/${encodeURIComponent(audiobookId)}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(updates)
        }
      );
      return await response.json();
    } catch (error) {
      console.error('Error updating audiobook playback state:', error);
      throw error;
    }
  }

  async updateChapterPlaybackState(audiobookId, chapterId, updates) {
    try {
      const response = await this.makeAuthenticatedRequest(
        `/api/playback/${encodeURIComponent(audiobookId)}/${encodeURIComponent(chapterId)}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(updates)
        }
      );
      return await response.json();
    } catch (error) {
      console.error('Error updating chapter playback state:', error);
      throw error;
    }
  }

  async updateChapterState(userId, audiobookId, chapterId, updates) {
    try {
      const chapterKey = this.generateKey(userId, audiobookId, 'chapters', `${chapterId}.json`);
      
      try {
        // Get current chapter state
        const chapterBlob = await this.downloadFile(chapterKey);
        const chapter = JSON.parse(await chapterBlob.text());

        // Apply updates
        const updatedChapter = {
          ...chapter,
          ...updates,
          lastModified: new Date().toISOString()
        };

        // Upload updated state
        const chapterJson = JSON.stringify(updatedChapter, null, 2);
        await this.uploadFile(chapterKey, chapterJson, 'application/json');

        // Clear cache
        this.cache.clear();

        return updatedChapter;
      } catch (error) {
        if (error.message.includes('401')) {
          return this.updateChapterState(userId, audiobookId, chapterId, updates);
        }
        throw error;
      }
    } catch (error) {
      console.error('Error updating chapter state:', error);
      throw error;
    }
  }

  async updateManifest(userId, audiobookId, updates) {
    try {
      console.log('Updating manifest:', { userId, audiobookId, updates });
      const manifestKey = this.generateKey(userId, audiobookId, '', 'manifest.json');
      
      try {
        // Get current manifest
        const manifestBlob = await this.downloadFile(manifestKey);
        if (!manifestBlob) {
          throw new Error('Failed to download current manifest');
        }

        const manifestText = await manifestBlob.text();
        const manifest = JSON.parse(manifestText);

        // Apply updates
        const updatedManifest = {
          ...manifest,
          ...updates,
          lastModified: new Date().toISOString()
        };

        // Validate by parsing and re-stringifying
        const manifestJson = JSON.stringify(updatedManifest, null, 2);
        JSON.parse(manifestJson); // Validate JSON is well-formed
        
        // Use URL pattern similar to delete endpoint
        const response = await this.makeAuthenticatedRequest(
          `/api/wasabi/manifest/${encodeURIComponent(userId)}/${encodeURIComponent(audiobookId)}`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json'
            },
            body: JSON.stringify(updates)  // Send only the updates
          }
        );

        if (!response.ok) {
          throw new Error(`Failed to update manifest: ${response.status}`);
        }

        // Clear specific cache entries
        const cacheKeys = [
          `audiobooks:${userId}`,  // Clear audiobooks list cache
          this.getCacheKey(manifestKey), // Clear manifest cache
          `audiobook:${userId}:${audiobookId}` // Clear single audiobook cache
        ];
        
        cacheKeys.forEach(key => {
          if (this.cache.has(key)) {
            this.cache.delete(key);
          }
        });

        return updatedManifest;
      } catch (error) {
        if (error.message.includes('401')) {
          return this.updateManifest(userId, audiobookId, updates);
        }
        throw error;
      }
    } catch (error) {
      console.error('Error updating manifest:', error);
      throw error;
    }
  }
}

const wasabiService = new WasabiService();
export default wasabiService;
